import * as React from 'react';
import Box from '@mui/material/Box';
import Autocomplete from '@mui/material/Autocomplete';
import Grid from '@mui/material/Grid';
import parse from 'autosuggest-highlight/parse';
import { debounce } from '@mui/material/utils';
import { UswfInput } from './UswfInput';
import { InputAdornment } from '@mui/material';

const autocompleteService = { current: null };

export default function AddressAutocomplete({isGoogleApiLoaded, onSelection}) {
  const [value, setValue] = React.useState(null);
  const [inputValue, setInputValue] = React.useState('');
  const [options, setOptions] = React.useState([]);
  
  const fetch = React.useMemo(
    () =>
      debounce((request, callback) => {
        autocompleteService.current.getPlacePredictions(request, callback);
      }, 400),
    [],
  );
  
  React.useEffect(() => {
    if(!isGoogleApiLoaded) {
      return undefined;
    }
    
    let active = true;
    
    if (!autocompleteService.current && window.google) {
      autocompleteService.current =
        new window.google.maps.places.AutocompleteService();
    }
    if (!autocompleteService.current) {
      return undefined;
    }
    
    if (inputValue === '') {
      setOptions(value ? [value] : []);
      return undefined;
    }
    
    fetch({ input: inputValue }, (results) => {
      if (active) {
        let newOptions = [];
        
        if (value) {
          newOptions = [value];
        }
        
        if (results) {
          newOptions = [...newOptions, ...results];
        }
        
        setOptions(newOptions);
      }
    });
    
    return () => {
      active = false;
    };
  }, [value, inputValue, fetch, isGoogleApiLoaded]);
  
  if(!isGoogleApiLoaded) {
    return null;
  }
  
  return (
    <Autocomplete
      id="google-map-demo"
      className="autocomplete-input"
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.description
      }
      filterOptions={(x) => x}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={value}
      noOptionsText="No locations"
      onChange={(event, newValue, reason, details) => {
        setOptions(newValue ? [newValue, ...options] : options);
        setValue(newValue);
        if(reason === 'selectOption') {
          onSelection(newValue);
        }
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <UswfInput
          {...params}
          InputProps={{
            ...params.InputProps,
            endAdornment: null,
            startAdornment: <InputAdornment className="address-icon-wrapper" position="start">
              <img className="address-icon" src="/img/svg_icons/location.svg" />
            </InputAdornment>
          }}
          placeholder="Enter an address"
          fullWidth
        />
      )}
      componentsProps={{
        popper: {
          className: 'address-popper'
        }
      }}
      renderOption={(props, option) => {
        const matches =
          option.structured_formatting.main_text_matched_substrings || [];
        
        const parts = parse(
          option.structured_formatting.main_text,
          matches.map((match) => [match.offset, match.offset + match.length]),
        );
        
        return (
          <li {...props}>
            <Grid container alignItems="center" className="autocomplete-item">
              <Grid item sx={{ width: '100%', wordWrap: 'break-word', color: 'white' }}>
                {parts.map((part, index) => (
                  <Box
                    key={index}
                    component="span"
                    sx={{ fontWeight: part.highlight ? 'bold' : 'regular' }}
                  >
                    {part.text}
                  </Box>
                ))},&nbsp;
                <Box component="span" variant="body2" sx={{ color: 'white' }}>
                  {option.structured_formatting.secondary_text}
                </Box>
              </Grid>
            </Grid>
          </li>
        );
      }}
    />
  );
}
